import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Loader2, Plus } from '@/components/icons';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from './ui/drawer';
import { Button } from './ui/button';
import { FormSkeleton } from './ui/skeleton-presets';
import { Stepper } from './ui/stepper';
import { PrntrCard } from './ui/prntr-input';
import {
  DateObjectRow,
  PhoneRowField,
  PickerRowField,
  SelectRowField,
  TextRow,
  TextareaField,
} from './ui/prntr-fields';
import { CountryCodePickerDrawer } from './CountryCodePickerDrawer';
import { NationalityPickerDrawer } from './NationalityPickerDrawer';
import { AirportPickerDrawer } from './AirportPickerDrawer';
import { DocumentFormDrawer } from './documents/DocumentFormDrawer';
import type { StagedDocument } from './documents/types';
import { getPassengerDocumentIcon } from './DocumentListCard';
import { DocumentsGroupCard } from './DocumentsGroupCard';
import { EmptyStateCard } from './EmptyStateCard';
import { parsePhoneNumber, formatPhoneNumber } from '../lib/phone';
import { PassengerRole } from '../../../api/src/shared';
import { DocumentSubType } from '@@api/shared';
import {
  usePassengerCreate,
  usePassengerUpdate,
  usePassengerGetById,
  usePassengerSetPhoto,
  useAirportByIcao,
} from '../lib/hooks';
import { useDocumentCreate } from '../lib/hooks/document';
import { generatePassengerPhotoS3Key, generateS3Key, uploadFileWithPresignedPost, useS3GetUploadPost } from '../lib/hooks/s3';
import { cn, getInitials } from '../lib/utils';
import { hapticLight } from '@/lib/haptics';
import { ProfileAvatar } from './ProfileAvatar';
import { ProfilePhotoPicker, ProfilePhotoPickerHandle } from './ProfilePhotoPicker';
import { PaxPreferencePhotos } from './PaxPreferencePhotos';
import { StagedPhotoGridHandle } from './StagedPhotoGrid';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { track, EVENTS } from '@/utils/analytics';

const roleOptions = [
  { value: PassengerRole.Primary, label: 'Primary' },
  { value: PassengerRole.Guest, label: 'Guest' },
];

// Create flow is a 3-step wizard. Steps 1–2 are slices of the one form; step 3
// stages documents locally. Nothing is written until Finish Setup on step 3,
// which creates the passenger and uploads photos + staged documents. Step 4 is
// the success card — not a stepper entry, it just sits past the last one so
// every circle reads as complete.
type PaxStep = 1 | 2 | 3 | 4;

const LAST_FORM_STEP = 2;
const DOCS_STEP = 3;
const SUCCESS_STEP = 4;

const STEP_LABELS = ['Personal', 'Preferences', 'Docs'] as const;

// Form validation schema
const paxFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  nationality: z.string().optional(),
  homeBaseIcao: z.string().optional(),
  phoneCountryCode: z.string(),
  phoneNumber: z.string().optional(),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  date: z.date().optional(),
  role: z.nativeEnum(PassengerRole),
  medicalInfo: z.string().optional(),
  cabinPreferences: z.string().optional(),
  beautyPreferences: z.string().optional(),
  foodPreferences: z.string().optional(),
  beveragePreferences: z.string().optional(),
  dislikes: z.string().optional(),
  generalInformation: z.string().optional(),
}).refine(
  (data) => {
    // Only validate phone if a number is provided
    if (!data.phoneNumber) return true;
    const fullNumber = `${data.phoneCountryCode}${data.phoneNumber}`;
    const phoneRegex = /^\+?[\d\s\-\(\)]{7,15}$/;
    return phoneRegex.test(fullNumber)
  },
  {
    message: 'Invalid phone number',
    path: ['phoneNumber'],
  }
);

type PaxFormData = z.infer<typeof paxFormSchema>;


// Props interface
interface PaxDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after save with the passenger's id (the new id in create mode). */
  onSuccess?: (passengerId?: string) => void;
  passengerId?: string; // If provided, edit mode; otherwise create mode
}

export function PaxDrawer({ isOpen, onClose, onSuccess, passengerId }: PaxDrawerProps) {
  const isEditMode = !!passengerId;

  // Picker drawer states
  const [isPhoneCodePickerOpen, setIsPhoneCodePickerOpen] = useState(false);
  const [isNationalityPickerOpen, setIsNationalityPickerOpen] = useState(false);
  const [isBasePickerOpen, setIsBasePickerOpen] = useState(false);

  // The wizard writes the passenger only at Finish Setup on the docs step. The
  // created id is kept just to hydrate the success card (avatar via
  // usePassengerGetById) and to treat dismissing that card as "done". Edits
  // always target the prop id.
  const [createdPassengerId, setCreatedPassengerId] = useState<string | null>(null);
  const activePassengerId = passengerId ?? createdPassengerId;

  // Create mode is a wizard; edit mode stays a single scrolling form with every
  // section visible at once.
  const [currentStep, setCurrentStep] = useState<PaxStep>(1);
  const [isAddDocumentOpen, setIsAddDocumentOpen] = useState(false);
  // Documents staged locally on step 3 (the passenger doesn't exist yet);
  // Finish Setup uploads them right after creating the record.
  const [stagedDocuments, setStagedDocuments] = useState<StagedDocument[]>([]);

  // Fetch passenger data once we have an id (edit mode, or post-create in the wizard)
  const { data: passengerData, isLoading: isLoadingPassenger } = usePassengerGetById(
    activePassengerId ?? undefined
  );

  // Mutations
  const { mutateAsync: createPassenger, isPending: isCreating } = usePassengerCreate();
  const { mutateAsync: updatePassenger, isPending: isUpdating } = usePassengerUpdate(passengerId ?? '');
  const setPhotoMutation = usePassengerSetPhoto();
  const getUploadPost = useS3GetUploadPost();
  const createDocumentMutation = useDocumentCreate();

  const isPending = isCreating || isUpdating;

  // Staged photos: profile picker + one grid per preference section. Uploads
  // flush after save so create mode has a passenger id to attach them to.
  const photoPickerRef = useRef<ProfilePhotoPickerHandle>(null);
  const prefPhotoRefs = useRef<Record<string, StagedPhotoGridHandle | null>>({});
  const [hasPendingPhoto, setHasPendingPhoto] = useState(false);
  const [isFlushingPhotos, setIsFlushingPhotos] = useState(false);
  const isSavingRef = useRef(false);
  const [pendingPrefPhotoCounts, setPendingPrefPhotoCounts] = useState<Record<string, number>>({});
  const pendingPrefPhotoTotal = Object.values(pendingPrefPhotoCounts).reduce((a, b) => a + b, 0);

  const photoS3Key = (passengerData as { photoS3Key?: string | null } | undefined)?.photoS3Key;
  const avatarColor = (passengerData as { avatarColor?: string | null } | undefined)?.avatarColor;

  const photosByPreference = useMemo(() => {
    const photos = (passengerData as { preferencePhotos?: { preference: string; s3Key: string }[] } | undefined)
      ?.preferencePhotos ?? [];
    return photos.reduce<Record<string, string[]>>((acc, p) => {
      acc[p.preference] = [...(acc[p.preference] ?? []), p.s3Key];
      return acc;
    }, {});
  }, [passengerData]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<PaxFormData>({
    resolver: zodResolver(paxFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      nationality: '',
      homeBaseIcao: '',
      phoneCountryCode: '+1',
      phoneNumber: '',
      email: '',
      date: undefined,
      role: PassengerRole.Primary,
      medicalInfo: '',
      cabinPreferences: '',
      beautyPreferences: '',
      foodPreferences: '',
      beveragePreferences: '',
      dislikes: '',
      generalInformation: '',
    },
    mode: 'onChange',
  });

  const phoneCountryCode = watch('phoneCountryCode');
  const nationality = watch('nationality');
  const homeBaseIcao = watch('homeBaseIcao');
  const firstName = watch('firstName');
  const lastName = watch('lastName');

  // Fetch selected airport for displaying base name
  const selectedAirport = useAirportByIcao(homeBaseIcao);

  // Populate form with passenger data in edit mode
  useEffect(() => {
    // Skip while saving: the update mutation's invalidation refetches
    // passengerData mid-flush and the reset() would clear isSubmitting/isDirty.
    if (isSavingRef.current) return;
    if (isEditMode && passengerData && isOpen) {
      const parsedPhone = parsePhoneNumber(passengerData.phone || '');
      reset({
        firstName: passengerData.firstName || '',
        lastName: passengerData.lastName || '',
        nationality: passengerData.nationality || '',
        homeBaseIcao: passengerData.homeBaseIcao || '',
        phoneCountryCode: parsedPhone.countryCode,
        phoneNumber: parsedPhone.number,
        email: passengerData.email || '',
        // Stored as UTC midnight; rebuild a local-midnight Date from the UTC
        // calendar day so timezones behind UTC don't drift a day per re-save.
        date: passengerData.dateOfBirth
          ? dayjs(dayjs.utc(passengerData.dateOfBirth).format('YYYY-MM-DD')).toDate()
          : undefined,
        role: passengerData.role,
        medicalInfo: passengerData.medicalInfo || '',
        cabinPreferences: passengerData.cabinPreferences || '',
        beautyPreferences: passengerData.beautyPreferences || '',
        foodPreferences: passengerData.foodPreferences || '',
        beveragePreferences: passengerData.beveragePreferences || '',
        dislikes: passengerData.dislikes || '',
        generalInformation: passengerData.generalInformation || '',
      });
    } else if (!isEditMode && isOpen && !createdPassengerId) {
      // Skipped once the passenger is created — the wizard is on its success
      // card and a re-fire here would throw the user back to step 1.
      reset({
        firstName: '',
        lastName: '',
        nationality: '',
        homeBaseIcao: '',
        phoneCountryCode: '+1',
        phoneNumber: '',
        email: '',
        date: undefined,
        role: PassengerRole.Primary,
        medicalInfo: '',
        cabinPreferences: '',
        beautyPreferences: '',
        foodPreferences: '',
        beveragePreferences: '',
        dislikes: '',
        generalInformation: '',
      });
      setCurrentStep(1);
    }
  }, [isEditMode, passengerData, isOpen, createdPassengerId, reset]);

  // Gate for the wizard's Next button. A derived boolean rather than RHF's
  // trigger(), matching CrewDrawer's isStep1Valid. Step 2 holds only optional
  // fields, so it never blocks.
  const isStep1Valid =
    !!firstName &&
    !!lastName &&
    !errors.firstName &&
    !errors.lastName &&
    // Both optional, but they live on this step — a malformed value has to block
    // Next rather than surface later as a silently-refused Create.
    !errors.email &&
    !errors.phoneNumber;

  // The Create button submits the whole form, so a bad value left behind on an
  // earlier step needs to be visible rather than silently blocking the submit.
  const stepErrors: Partial<Record<PaxStep, boolean>> = {
    1: !!(errors.firstName || errors.lastName || errors.email || errors.phoneNumber),
  };

  const handleNext = () =>
    setCurrentStep((step) => (Math.min(step + 1, DOCS_STEP) as PaxStep));
  const handleBack = () => setCurrentStep((step) => (Math.max(step - 1, 1) as PaxStep));

  // Visible in edit mode (single scrolling page) or when it's the active create step
  const sectionHidden = (step: PaxStep) => !isEditMode && currentStep !== step;

  // Photo uploads/removals run after the save so create mode has an id to
  // attach them to. Failures only toast — the passenger record is already saved
  // (each grid also toasts per file).
  const flushPhotos = async (id: string) => {
    setIsFlushingPhotos(true);
    try {
      try {
        await photoPickerRef.current?.flushPending(id);
      } catch (error) {
        console.error('Failed to save passenger photo:', error);
        toast.error('Failed to save photo');
      }
      for (const ref of Object.values(prefPhotoRefs.current)) {
        try {
          await ref?.flushPending(id);
        } catch (error) {
          console.error('Failed to save preference photos:', error);
        }
      }
    } finally {
      setIsFlushingPhotos(false);
    }
  };

  // Staged documents upload right after the passenger is created (mirrors
  // CrewDrawer's flush): the caller toasts on failure but keeps going — the
  // passenger record already exists.
  const flushStagedDocuments = async (id: string) => {
    for (const staged of stagedDocuments) {
      const s3Key = generateS3Key(id, staged.subType ?? DocumentSubType.Other, staged.file.name);
      await uploadFileWithPresignedPost(
        staged.file,
        s3Key,
        getUploadPost.mutateAsync as unknown as (input: {
          s3Key: string;
          contentType: string;
        }) => Promise<{ url: string; fields: Record<string, string> }>
      );
      await createDocumentMutation.mutateAsync({
        passengerId: id,
        category: 'Passenger',
        title: staged.title,
        fileName: staged.file.name,
        subType: staged.subType,
        s3Key,
        documentNumber: staged.documentNumber || null,
        issuer: staged.issuer || null,
        issueDate: staged.issueDate,
        expiryDate: staged.expiryDate ?? undefined,
        isPublic: staged.isPublic,
      });
    }
  };

  const onFormSubmit = async (data: PaxFormData) => {
    isSavingRef.current = true;
    // Cleared fields must be sent as null, not undefined: the update input is a
    // partial schema and Prisma ignores undefined keys, so `|| undefined` would
    // silently leave the old value in the database.
    const submitData = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email || null,
      phone: formatPhoneNumber(data.phoneCountryCode, data.phoneNumber) || null,
      nationality: data.nationality || null,
      homeBaseIcao: data.homeBaseIcao || null,
      role: data.role,
      medicalInfo: data.medicalInfo || null,
      foodPreferences: data.foodPreferences || null,
      cabinPreferences: data.cabinPreferences || null,
      beautyPreferences: data.beautyPreferences || null,
      beveragePreferences: data.beveragePreferences || null,
      dislikes: data.dislikes || null,
      generalInformation: data.generalInformation || null,
      // Format from local calendar fields — toISOString() would shift the day
      // for timezones ahead of UTC (local midnight falls on the previous UTC day).
      date: data.date ? dayjs(data.date).format('YYYY-MM-DD') : null,
    };

    try {
      if (!isEditMode) {
        try {
          const created = await createPassenger(submitData);
          const createdId = (created as { id?: string } | undefined)?.id;
          track(EVENTS.PAX_CREATED)
          if (createdId) {
            await flushPhotos(createdId);
            // Doc uploads are non-fatal past this point — the passenger exists,
            // so a failed upload warns rather than aborting the wizard.
            try {
              await flushStagedDocuments(createdId);
            } catch (error) {
              console.error('Failed to upload staged documents:', error);
              toast.error('Passenger added, but some documents could not be uploaded');
            }
            // Stay open on the success card; Done (or dismissing) finishes up.
            setCreatedPassengerId(createdId);
            setCurrentStep(SUCCESS_STEP);
            return;
          }
          // No id came back, so there's nothing to attach documents to. Close
          // out rather than stranding the wizard on the docs step.
          toast.success('Passenger added successfully');
          resetForm();
          onClose();
          onSuccess?.(undefined);
        } catch (error) {
          console.error('Error adding passenger:', error);
          toast.error('Failed to add passenger');
        }
      } else if (passengerId) {
        try {
          await updatePassenger({ id: passengerId, ...submitData });
          await flushPhotos(passengerId);
          toast.success('Passenger updated successfully');
          reset();
          onClose();
          onSuccess?.(passengerId);
        } catch (error) {
          console.error('Error updating passenger:', error);
          toast.error('Failed to update passenger');
        }
      }
    } finally {
      isSavingRef.current = false;
    }
  };

  const resetForm = () => {
    reset();
    setCurrentStep(1);
    setCreatedPassengerId(null);
    setIsAddDocumentOpen(false);
    setStagedDocuments([]);
  };

  const handleAddDocument = () => {
    hapticLight();
    setIsAddDocumentOpen(true);
  };

  // Finish the create wizard from the success card. The passenger already
  // exists by then, so dismissing is a "done", not a "discard".
  const handleFinish = () => {
    const newId = createdPassengerId ?? undefined;
    resetForm();
    onClose();
    onSuccess?.(newId);
  };

  const handleCancel = () => {
    if (isSaving) return;
    // createdPassengerId only exists once Finish Setup has run, i.e. on the
    // success card — dismissing there is a "done", not a "discard".
    if (!isEditMode && createdPassengerId) {
      handleFinish();
      return;
    }
    resetForm();
    onClose();
  };

  const hasPendingPhotos = hasPendingPhoto || pendingPrefPhotoTotal > 0;
  // isFlushingPhotos is plain state: the update mutation's invalidation
  // refetches passengerData mid-flush and RHF's reset() clears isSubmitting,
  // so isSubmitting alone doesn't cover the photo-upload window.
  const isSaving = isSubmitting || isPending || isFlushingPhotos;
  const isSubmitDisabled = isSaving || (!isDirty && !hasPendingPhotos) || !isValid;

  // Photos under a preference textarea (compact camera-button trigger per the
  // design); keys match the API's PreferenceKeySchema.
  const renderPreferencePhotos = (preference: string) => (
    <div className="pb-3">
      <PaxPreferencePhotos
        key={`${preference}-${passengerId ?? 'new'}`}
        ref={(r) => { prefPhotoRefs.current[preference] = r; }}
        preference={preference}
        s3Keys={photosByPreference[preference] ?? []}
        trigger="icon"
        onPendingChange={(count) =>
          setPendingPrefPhotoCounts((cur) => (cur[preference] === count ? cur : { ...cur, [preference]: count }))
        }
      />
    </div>
  );

  // Show loading state when fetching passenger data in edit mode
  if (isEditMode && isLoadingPassenger) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="bg-surface-L0 max-h-[85dvh]">
          <FormSkeleton fields={6} />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <>
      <Drawer open={isOpen} onOpenChange={(open) => { if (!open) handleCancel(); }}>
        <DrawerContent className="bg-surface-L0 flex flex-col max-h-[85dvh]">
          {/* Title and stepper share the sticky header: DrawerContent drops every
              non-footer child into one scroll area, so a stepper outside this
              block would scroll up out of reach behind the pinned title. */}
          <DrawerHeader className="flex flex-col gap-0 p-0 shrink-0">
            <div className="flex items-center justify-between p-4">
              <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
                {isEditMode ? 'Edit Passenger' : 'Add Passenger'}
              </DrawerTitle>
              <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={handleCancel}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            {!isEditMode && (
              <Stepper
                className="pb-3"
                steps={STEP_LABELS.map((label, index) => ({
                  label,
                  hasError: stepErrors[(index + 1) as PaxStep],
                }))}
                currentStep={currentStep}
                // Withheld only on the terminal success card. Navigation is
                // otherwise free: nothing is written until Finish Setup, which
                // carries the full validity gate.
                onStepClick={
                  currentStep <= DOCS_STEP
                    ? (step) => setCurrentStep(step as PaxStep)
                    : undefined
                }
              />
            )}
          </DrawerHeader>

          <div className="flex-1 min-h-0 overflow-y-scroll px-4">
            {/* Every section stays mounted and is hidden with CSS: unmounting would
                drop the staged preference photos and the profile photo ref, and the
                final submit needs every form step's values. */}
            <div
              className={cn(
                'flex flex-col gap-4 pb-4',
                isSaving && 'pointer-events-none',
                !isEditMode && currentStep > LAST_FORM_STEP && 'hidden'
              )}
            >
              {/* Step 1 — Personal */}
              <div className={cn('flex flex-col gap-4', sectionHidden(1) && 'hidden')}>
                {/* Profile Photo */}
                <ProfilePhotoPicker
                  ref={photoPickerRef}
                  key={passengerId ?? 'new'}
                  currentS3Key={photoS3Key}
                  initials={getInitials(firstName, lastName)}
                  avatarColor={avatarColor}
                  generateS3Key={generatePassengerPhotoS3Key}
                  setPhoto={setPhotoMutation.mutateAsync as unknown as (input: { id: string; s3Key: string | null }) => Promise<unknown>}
                  onPendingChange={setHasPendingPhoto}
                />

                {/* Details */}
                <PrntrCard>
                  <TextRow
                    control={control}
                    name="firstName"
                    label="First Name *"
                    placeholder="John"
                    nullable={false}
                    error={errors.firstName?.message}
                  />
                  <TextRow
                    control={control}
                    name="lastName"
                    label="Last Name *"
                    placeholder="Smith"
                    nullable={false}
                    error={errors.lastName?.message}
                  />
                  <PickerRowField
                    control={control}
                    name="nationality"
                    label="Nationality"
                    display={(value) => (value as string) || null}
                    onOpen={() => setIsNationalityPickerOpen(true)}
                    hideChevron
                  />
                  <PickerRowField
                    control={control}
                    name="homeBaseIcao"
                    label="Base"
                    display={(value) =>
                      selectedAirport
                        ? `${selectedAirport.icaoCode} · ${selectedAirport.name}`
                        : ((value as string) || null)
                    }
                    onOpen={() => setIsBasePickerOpen(true)}
                    hideChevron
                  />
                  <PhoneRowField
                    control={control}
                    codeName="phoneCountryCode"
                    numberName="phoneNumber"
                    label="Telephone Number"
                    onCodeClick={() => setIsPhoneCodePickerOpen(true)}
                    error={errors.phoneNumber?.message}
                  />
                  <TextRow
                    control={control}
                    name="email"
                    label="Email Address"
                    type="email"
                    inputMode="email"
                    autoCapitalize="none"
                    nullable={false}
                    error={errors.email?.message}
                  />
                  <DateObjectRow
                    control={control}
                    name="date"
                    label="Date of Birth"
                    title="Date of Birth"
                    startMonth={new Date(1920, 0)}
                    endMonth={new Date()}
                  />
                  <SelectRowField
                    control={control}
                    name="role"
                    label="Role"
                    options={roleOptions}
                  />
                </PrntrCard>
              </div>

              {/* Step 2 — Preferences: textarea + staged photo grid each */}
              <div className={cn('flex flex-col', sectionHidden(2) && 'hidden')}>
                <TextareaField control={control} name="medicalInfo" label="Medical Information" />
                {renderPreferencePhotos('medicalInfo')}
                <TextareaField control={control} name="cabinPreferences" label="Cabin Preferences" />
                {renderPreferencePhotos('cabinPreferences')}
                <TextareaField control={control} name="beautyPreferences" label="Beauty Preferences" />
                {renderPreferencePhotos('beautyPreferences')}
                <TextareaField control={control} name="foodPreferences" label="Food Preferences" />
                {renderPreferencePhotos('foodPreferences')}
                <TextareaField control={control} name="beveragePreferences" label="Beverage Preferences" />
                {renderPreferencePhotos('beveragePreferences')}
                <TextareaField control={control} name="dislikes" label="Dislikes" />
                {renderPreferencePhotos('dislikes')}
                <TextareaField control={control} name="generalInformation" label="General Information" />
                {renderPreferencePhotos('generalInformation')}
              </div>
            </div>

            {/* Step 3 (create only): documents staged locally — the passenger
                doesn't exist yet, so tapping a row removes it rather than
                opening a viewer */}
            {!isEditMode && currentStep === DOCS_STEP && (
              <div className="flex flex-col gap-3 pb-4 pt-2">
                {stagedDocuments.length > 0 ? (
                  <>
                    <DocumentsGroupCard
                      name={`${firstName} ${lastName}`.trim() || 'New passenger'}
                      documents={stagedDocuments.map((doc, index) => ({
                        id: String(index),
                        title: doc.title,
                        subType: doc.subType,
                        expiryDate: doc.expiryDate,
                      }))}
                      getIcon={getPassengerDocumentIcon}
                      onDocumentClick={(documentId) =>
                        setStagedDocuments((prev) => prev.filter((_, i) => i !== Number(documentId)))
                      }
                    />
                    <Button
                      variant="secondary"
                      size="mini"
                      className="self-start text-content-brand-bold"
                      onClick={handleAddDocument}
                    >
                      <Plus className="size-4" />
                      Add Document
                    </Button>
                  </>
                ) : (
                  <EmptyStateCard label="Add a document" onAdd={handleAddDocument} />
                )}
              </div>
            )}

            {/* Step 4 (create only): terminal success card. Not a stepper entry —
                currentStep sits past the last circle so they all read complete. */}
            {!isEditMode && currentStep === SUCCESS_STEP && (
              <div className="flex flex-col items-center gap-4 px-8 pb-6 pt-8">
                <div className="relative">
                  <ProfileAvatar
                    photoS3Key={photoS3Key}
                    avatarColor={avatarColor}
                    initials={getInitials(firstName, lastName)}
                    className="size-8"
                    fallbackClassName="text-sm text-content-primary-inverse"
                  />
                  {/* 6px halo in the avatar's own colour at 40% (design: border-6) */}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-full"
                    style={
                      avatarColor
                        ? { boxShadow: `0 0 0 6px ${avatarColor}66` }
                        : undefined
                    }
                  />
                </div>
                <p className="text-center text-xl font-bold tracking-[-1px] text-content-primary-bold">
                  Passenger Added
                </p>
                <p className="text-center text-sm text-content-primary-subtle">
                  {`${firstName} ${lastName}`.trim() || 'They'} has been added to your passengers.
                </p>
                <Button size="mini" onClick={handleFinish} className="px-6">
                  Done
                </Button>
              </div>
            )}
          </div>

          {/* Footer — omitted on the success card, which carries its own Done */}
          {currentStep !== SUCCESS_STEP && (
            <DrawerFooter className="flex flex-row gap-2 p-4 shrink-0 border-t-0">
              {isEditMode ? (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    size="mini"
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="mini"
                    disabled={isSubmitDisabled}
                    onClick={handleSubmit(onFormSubmit)}
                    className="flex-1"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                  </Button>
                </>
              ) : currentStep === DOCS_STEP ? (
                <Button
                  type="button"
                  size="mini"
                  onClick={handleSubmit(onFormSubmit)}
                  disabled={!isValid || isSaving}
                  className="w-full"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Finish Setup'}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    size="mini"
                    onClick={currentStep === 1 ? handleCancel : handleBack}
                    disabled={isSaving}
                    className="flex-1"
                  >
                    {currentStep === 1 ? 'Cancel' : 'Back'}
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="mini"
                    onClick={handleNext}
                    disabled={currentStep === 1 && !isStep1Valid}
                    className="flex-1"
                  >
                    Next
                  </Button>
                </>
              )}
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>

      {/* Phone Country Code Picker */}
      <CountryCodePickerDrawer
        isOpen={isPhoneCodePickerOpen}
        onClose={() => setIsPhoneCodePickerOpen(false)}
        selectedCode={phoneCountryCode}
        onSelect={(code) => setValue('phoneCountryCode', code, { shouldDirty: true, shouldValidate: true })}
      />

      {/* Nationality Picker */}
      <NationalityPickerDrawer
        isOpen={isNationalityPickerOpen}
        onClose={() => setIsNationalityPickerOpen(false)}
        selectedNationality={nationality}
        onSelect={(nat) => setValue('nationality', nat, { shouldDirty: true, shouldValidate: true })}
      />

      {/* Airport/Base Picker */}
      <AirportPickerDrawer
        isOpen={isBasePickerOpen}
        onClose={() => setIsBasePickerOpen(false)}
        onSelect={(icaoCode) => setValue('homeBaseIcao', icaoCode, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        })}
        title="Select Base"
        searchParam={homeBaseIcao}
      />

      {/* Documents step (create wizard): the passenger doesn't exist yet, so
          the drawer stages the form values + File locally instead of uploading */}
      {!isEditMode && (
        <DocumentFormDrawer
          isOpen={isAddDocumentOpen}
          owner={{ type: 'passenger' }}
          onClose={() => setIsAddDocumentOpen(false)}
          onStage={(doc) => setStagedDocuments((prev) => [...prev, doc])}
        />
      )}
    </>
  );
}
